import { defineComponent, h, withDirectives, resolveDirective } from 'vue';
import { QInput } from 'quasar';
import BaseComponent from './baseFormMixin.jsx';
import utils from '../../../Shared/utils.jsx';
import { toggleNativeDatePicker, dismissNativeDatePicker } from './nativeDatePickerToggle.js';

import { appSettings } from '@/Shared/appSettings.js';
import TranslationContainer from './../TranslationContainer.jsx';

export default defineComponent({
    name: 'sform-date',
    mixins: [BaseComponent],
    components: {
        TranslationContainer,
        QInput,
    },
    data: () => ({
        pickervisible: false,
        nativePickerOpen: false,
        nativePickerSuppressUntil: 0,
    }),
    props: {
    },
    created() {
    },
    mounted() {
        this.loadComplete();
    },
    computed: {
        datevalue() {
            return this.getFormattedDate(this.itemvalue, true);
        },
        // Same defensive pattern as formSingleDatePicker.jsx / formDateRangePicker.jsx
        // (CCDEV-18145): QField/native input model-value here is a locally-formatted
        // display string, not necessarily the raw stored value. Validate this.itemvalue
        // directly so a future stricter format/pattern check can't silently fail here.
        rules() {
            const r = [];
            if (this.element?.required)
                r.push(() => {
                    const v = this.itemvalue;
                    return (v === null || v === undefined || v === '') ? 'Required' : true;
                });

            if (this.element?.schema)
                r.push(() => {
                    const errors = utils.schema.performValidation(this.element.schema, this.itemvalue);
                    return errors.length > 0 ? errors[0].error : true;
                });

            return r;
        },
    },
    methods: {
        saveDate() {
            this.pickervisible = false;
        },
        cancelDate() {
            this.pickervisible = false;
        },
        dateChanged(value) {
            // Ex: "2021-03" or "2021-06-17"
        },
        getFormattedDate(value, accountForTimezone) {
            if (!value) return value;

            // Don't try to parse incomplete dates
            if (value.length < 10) return value;

            // Check if we have a properly formatted date string
            const parts = value.split(/[-/.]/);
            if (parts.length !== 3) return value;

            // Ensure we have complete year, month, day values
            if (parts.some(part => !part)) return value;

            let yearStr = parts[0].replace(/^0+/, '');
            let monthStr = parts[1];
            let dayStr = parts[2];
            
            if (dayStr.length == 4 && yearStr.length == 2) {
                // swap the day and year since the format coming in isn't ISO format, only happens when we initialize.
                const temp = yearStr;
                yearStr = dayStr;
                dayStr = temp;
            }

            if (yearStr.length < 4 || monthStr.length !== 2 || dayStr.length !== 2) return value;

            var parseValue = value;

            if (yearStr.length > 4) {
                const sliceIndex = yearStr.length - 4;
                parseValue = parseValue.slice(sliceIndex);
            }

            const dt = new Date(Date.parse(parseValue));

            if (accountForTimezone) {
                const offset = dt.getTimezoneOffset();
                dt.setMinutes(dt.getMinutes() + offset);
            }

            const year = dt.getFullYear();
            const month = dt.getMonth() + 1;
            const day = dt.getDate();

            const str = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            console.log(`Formatting ${value} to ${str} using ${dt.toString()}`);
            return str;
        },
        sync_fromlocaldate(value) {
            // Keep picker open-state until cancel/blur/toggle-close (see formDateTime).
            const str = this.getFormattedDate(value, true);

            utils.debug(`DatePicker writing date to model value:'${value}' as '${str}'`);

            this.sync(str);
        },
        toggleNativePicker(e) {
            if (this.readonly || this.noinput || this.appearance?.readonly)
                return;
            const next = toggleNativeDatePicker(e, {
                isOpen: this.nativePickerOpen,
                suppressUntil: this.nativePickerSuppressUntil,
            }, this.dismissNativePicker);
            this.nativePickerOpen = next.isOpen;
            this.nativePickerSuppressUntil = next.suppressUntil;
        },
        dismissNativePicker() {
            const next = dismissNativeDatePicker({
                isOpen: this.nativePickerOpen,
                suppressUntil: this.nativePickerSuppressUntil,
            });
            this.nativePickerOpen = next.isOpen;
            this.nativePickerSuppressUntil = next.suppressUntil;
        },
        handleBlur(e) {
            this.dismissNativePicker();
            this.onBlur(e);
        },
    },
    render() {
        let slots = {
            message: ({message}) => {
                return <TranslationContainer context={this} value={message}></TranslationContainer>
            }
        }
        if (this.hasInputLabel)
            slots.label = () => <TranslationContainer context={this} value={this.labelText}></TranslationContainer>;

        let input = (
            <QInput
                class="caption q-pa-none q-ma-none sform-input-white sform-nativedate-input"
                style={{ width: "100%" }}
                dense
                outlined
                labelSlot={this.hasInputLabel}
                modelValue={this.datevalue}
                rules={this.rules} {...this.compilerErrorFieldProps}
                type="date"
                onUpdate:modelValue={(value) => this.sync_fromlocaldate(value)}
                onClick={this.toggleNativePicker}
                onCancel={this.dismissNativePicker}
                hint={this.inputHint}
                hide-hint
                bottom-slots={this.showFieldBottomSlots}
                onBlur={this.handleBlur}
                onFocus={this.onFocus}
                persistent-hint={appSettings.DebugTranslationPrefixSetting}
            >{slots}</QInput>
        );

        if (this.appearance)
            utils.applyVue2ToVue3Props(input, this.appearance);

        // if (this.directives && this.directives.some(a => a.name == 'on-enter-default'))
        //    input.props.autofocus = true;

        if (this.directives) {
            var applyDirectives = this.directives.map(d => {
                const dirDef = d.directive || resolveDirective(d.name);

                return [dirDef, d.value, d.arg, d.modifiers];
            });
            input = withDirectives(input, applyDirectives);
        }

        return input;
    }
});